import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FileRef } from '@/types/file';
import { READ_YOUR_WRITES_WINDOW_MS, lastWriteAt } from '@/api/readYourWrites';
import { sameFileRef } from './sameFileRef';

/**
 * How long a save stays unconfirmed while every read still returns the ref it
 * replaced (#885a-4 R2). After this the client stops insisting and trusts the
 * reads; a file stuck that long is #885 D7's accepted case.
 */
export const UNCONFIRMED_SAVE_BOUND_MS = 5 * 60 * 1000;

/**
 * The slot's query state when the write was accepted (#939 I7):
 * `dataUpdateCount` and whether a fetch was in flight (`fetchStatus ===
 * 'fetching'`). A fetch in flight at mark time was issued before the write,
 * so its answer is never a post-write read.
 */
export interface SaveMarkRead {
  updateCount: number;
  fetching: boolean;
}

/**
 * The query state of the read being judged (#939 I7): `dataUpdateCount` and
 * `dataUpdatedAt` of the data the surface shows.
 */
export interface SaveRead {
  updateCount: number;
  updatedAt: number;
}

interface SaveRecord {
  /** The FileRef the save replaced: what a stale read hands back. */
  replaced: FileRef | null | undefined;
  /** The FileRef the write's own response carried (the cache seed). */
  saved: FileRef | null | undefined;
  /** `lastWriteAt()` when the write was marked (0 = none noted). */
  wroteAt: number;
  /** A read with `updateCount` above this is a post-write read; undefined without a mark-time read. */
  baseline: number | undefined;
  /** `updateCount` of the first post-write read that equalled the seed (I7b). */
  seenSeedAt: number | undefined;
}

/**
 * Per-slot save records (#939): one record per file slot (component,
 * question, choice, assignment), each confirmed, bounded and cleared on its
 * own (I3).
 */
export interface UnconfirmedSaves<K extends string | number> {
  /**
   * Record an ACCEPTED write on slot `key`: `replaced` is the ref the slot
   * showed when the save started, `saved` the ref the write's own response
   * seeded (undefined when it carried none). `read` is the slot's query
   * state at this moment (after the seed was written to the cache); without
   * it the slot follows the #885a-4 rule alone. Re-marking a slot restarts
   * its bound.
   */
  markSaved: (
    key: K,
    replaced: FileRef | null | undefined,
    saved: FileRef | null | undefined,
    read?: SaveMarkRead,
  ) => void;
  /**
   * True while `current` is a stale read of slot `key`: the ref its save
   * replaced, before any caught-up read and inside the bound. A read that is
   * neither the replaced ref nor the seed confirms the slot (clears it), and
   * so does a CAUGHT-UP read whatever it equals (I7): a post-write read that
   * landed within READ_YOUR_WRITES_WINDOW_MS of the write (writer-pinned), or
   * one that follows a post-write read of the seed. Pass `read` (the shown
   * data's query state) to apply I7.
   */
  unconfirmed: (key: K, current: FileRef | null | undefined, read?: SaveRead) => boolean;
  /**
   * `unconfirmed` for EVERY entry, no short-circuit (each call is what
   * confirms its slot); true when any is. Feed it to `useFileRefRefetch`'s
   * `unconfirmed` option.
   */
  anyUnconfirmed: (entries: Iterable<readonly [K, FileRef | null | undefined]>, read?: SaveRead) => boolean;
  /**
   * The seed of slot `key`'s unconfirmed save (what the write's response
   * carried), for masking a stale slot (I2). Undefined when the slot has no
   * record or its write carried no ref.
   */
  seed: (key: K) => FileRef | null | undefined;
  /** Forget slot `key`'s save (the file was removed: nothing left to confirm). */
  clear: (key: K) => void;
  /**
   * Bumped on every mark, clear and expiry. A memo that masks by
   * `unconfirmed` depends on it (or on this object, whose identity changes
   * only with it), so an expiry repaints even when the read never changed.
   */
  version: number;
}

/**
 * #939 (I2–I4, I7): after a save over a file slot, keep re-reading until the
 * server reports the NEW file, and treat a re-read that hands back the
 * REPLACED ref (a lagging replica) as stale: it keeps the poll going and
 * paints nothing. Keyed by slot so several saves in flight are tracked
 * independently.
 *
 * The record lives in a ref: confirming during render is an idempotent ref
 * write, so StrictMode's double render cannot diverge. Bound timers live in
 * an effect over the records, never in `markSaved`, so a StrictMode remount
 * re-creates them.
 *
 * @example
 * const saves = useUnconfirmedSaves<number>();
 * useFileRefRefetch(refs, refetch, { unconfirmed: saves.anyUnconfirmed(entries, read) });
 * // in the write's onSuccess, after the seed is in the cache:
 * saves.markSaved(componentId, replaced, saved, { updateCount, fetching });
 */
export function useUnconfirmedSaves<K extends string | number>(): UnconfirmedSaves<K> {
  const recordsRef = useRef<Map<K, SaveRecord>>(new Map());
  const timersRef = useRef<Map<SaveRecord, ReturnType<typeof setTimeout>>>(new Map());
  // Marking, clearing and expiring change what `unconfirmed` answers; the
  // version re-renders the owner so its polling and masking pick that up.
  const [version, setVersion] = useState(0);

  const markSaved = useCallback<UnconfirmedSaves<K>['markSaved']>((key, replaced, saved, read) => {
    recordsRef.current.set(key, {
      replaced,
      saved,
      wroteAt: lastWriteAt(),
      baseline: read ? read.updateCount + (read.fetching ? 1 : 0) : undefined,
      seenSeedAt: undefined,
    });
    setVersion((n) => n + 1);
  }, []);

  const clear = useCallback<UnconfirmedSaves<K>['clear']>((key) => {
    if (!recordsRef.current.delete(key)) return;
    setVersion((n) => n + 1);
  }, []);

  const unconfirmed = useCallback<UnconfirmedSaves<K>['unconfirmed']>((key, current, read) => {
    const records = recordsRef.current;
    const record = records.get(key);
    if (!record) return false;
    if (read && record.baseline !== undefined && read.updateCount > record.baseline) {
      // A post-write read. Caught up (I7) when writer-pinned (a) or when it
      // follows a post-write read of the seed (b): the truth, whatever it equals.
      const pinned = record.wroteAt > 0 && read.updatedAt - record.wroteAt < READ_YOUR_WRITES_WINDOW_MS;
      const afterSeed = record.seenSeedAt !== undefined && read.updateCount > record.seenSeedAt;
      if (pinned || afterSeed) {
        records.delete(key);
        return false;
      }
      if (record.seenSeedAt === undefined && sameFileRef(current, record.saved)) {
        record.seenSeedAt = read.updateCount;
      }
    }
    if (sameFileRef(current, record.replaced)) return true;
    // Not stale. A read that is not the seed either is the server's word on
    // the new file: the save is confirmed, and a later ref equal to the
    // replaced one is no longer a stale read.
    if (!sameFileRef(current, record.saved)) records.delete(key);
    return false;
  }, []);

  const anyUnconfirmed = useCallback<UnconfirmedSaves<K>['anyUnconfirmed']>(
    (entries, read) => {
      let any = false;
      for (const [key, current] of entries) {
        // No short-circuit: every call may confirm its own slot.
        if (unconfirmed(key, current, read)) any = true;
      }
      return any;
    },
    [unconfirmed],
  );

  const seed = useCallback<UnconfirmedSaves<K>['seed']>((key) => recordsRef.current.get(key)?.saved, []);

  // The bound: one clock per record (re-marking a slot replaces its record,
  // so its clock restarts). Reconciled on every version change.
  useEffect(() => {
    const records = recordsRef.current;
    const timers = timersRef.current;
    const live = new Set(records.values());
    for (const [record, timer] of timers) {
      if (!live.has(record)) {
        clearTimeout(timer);
        timers.delete(record);
      }
    }
    for (const [key, record] of records) {
      if (timers.has(record)) continue;
      timers.set(
        record,
        setTimeout(() => {
          timers.delete(record);
          if (records.get(key) !== record) return;
          records.delete(key);
          setVersion((n) => n + 1);
        }, UNCONFIRMED_SAVE_BOUND_MS),
      );
    }
  }, [version]);

  // Unmount (and StrictMode's simulated one) drops every clock; the effect
  // above re-creates them for the records on remount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  return useMemo(
    () => ({ markSaved, unconfirmed, anyUnconfirmed, seed, clear, version }),
    [markSaved, unconfirmed, anyUnconfirmed, seed, clear, version],
  );
}

export interface UnconfirmedSave {
  /**
   * Record an ACCEPTED write: `replacedRef` is the ref on screen when the save
   * started, `savedRef` the ref the write's own response seeded (undefined
   * when it carried none).
   */
  markSaved: (replacedRef: FileRef | null | undefined, savedRef: FileRef | null | undefined) => void;
  /**
   * True while `currentRef` is the ref the save replaced (a stale re-read)
   * and the save is younger than {@link UNCONFIRMED_SAVE_BOUND_MS}. The first
   * read that is neither the replaced ref nor the write's own seed confirms
   * the save and clears the record, so a later terminal status ends polling
   * normally. Call it once per render with the ref the surface shows.
   */
  unconfirmed: (currentRef: FileRef | null | undefined) => boolean;
  /** Forget the save (the file was removed: nothing left to confirm). */
  clear: () => void;
}

const SINGLE_SLOT = 0;

/**
 * I4 (#885): after a save, keep re-reading until the server reports the NEW
 * file terminal. A re-read can hit a lagging replica and hand back the ref
 * the save replaced (for example the OLD `failed` file); nothing in that ref
 * is processing, so `useFileRefRefetch` alone would stop. Feed
 * `unconfirmed(ref)` to its `unconfirmed` option.
 *
 * The seed (`savedRef`) is not a read: a read equal to it is indistinguishable
 * from the seed itself, so it neither confirms nor counts as stale. The next
 * read that differs from both (the new file's terminal status, or anything
 * else the server now says) confirms.
 *
 * The single-slot form of {@link useUnconfirmedSaves}; the returned object
 * has a stable identity.
 *
 * @example
 * const avatarSave = useUnconfirmedSave();
 * useFileRefRefetch([user?.avatar], refetch, { unconfirmed: avatarSave.unconfirmed(user?.avatar) });
 * // after the write resolves:
 * avatarSave.markSaved(replaced, response.avatar);
 */
export function useUnconfirmedSave(): UnconfirmedSave {
  const { markSaved, unconfirmed, clear } = useUnconfirmedSaves<typeof SINGLE_SLOT>();
  return useMemo(
    () => ({
      markSaved: (replaced, saved) => markSaved(SINGLE_SLOT, replaced, saved),
      unconfirmed: (current) => unconfirmed(SINGLE_SLOT, current),
      clear: () => clear(SINGLE_SLOT),
    }),
    [markSaved, unconfirmed, clear],
  );
}
